import {
  assetConditionSchema,
  buildingTypeSchema,
  type InterestConfigType,
  type PropertyFiltersType,
} from '@aikoa/common'
import { getCurrentLocale } from '@aikoa/i18n'
import { useTranslations } from '@aikoa/i18n/react'
import { useMemo } from 'react'
import { getBuildingTypeLabel, getConditionLabel } from '~/lib/assets'
import { MultiSelectChips } from './inputs/MultiSelectChips'
import { NumberRangeInput } from './inputs/NumberRangeInput'

const locale = getCurrentLocale()

export function InterestFilterEditor({
  editor,
  filters,
  onChange,
  className,
  inputClassName,
}: {
  editor: InterestConfigType
  filters: PropertyFiltersType
  onChange: (next: PropertyFiltersType) => void
  className?: string
  inputClassName?: string
}) {
  const { t } = useTranslations(['interest', 'common'])
  const { t: tProperty } = useTranslations('property')

  const buildingTypeOptions = useMemo(
    () =>
      buildingTypeSchema.options.map((typeLiteral) => ({
        value: typeLiteral.value,
        label: getBuildingTypeLabel({
          buildingType: typeLiteral.value,
          t: tProperty,
        }),
      })),
    [tProperty]
  )

  const conditionOptions = useMemo(
    () =>
      assetConditionSchema.options.map((conditionLiteral) => ({
        value: conditionLiteral.value,
        label: getConditionLabel(conditionLiteral.value, tProperty),
      })),
    [tProperty]
  )

  if (editor === 'buildingType') {
    return (
      <MultiSelectChips
        label={t(($) => $.labels.buildingType)}
        options={buildingTypeOptions}
        selected={filters.buildingTypes ?? []}
        onToggle={(value) =>
          onChange({
            ...filters,
            buildingTypes: toggleValue(filters.buildingTypes ?? [], value),
          })
        }
        className={className}
      />
    )
  }

  if (editor === 'condition') {
    return (
      <MultiSelectChips
        label={t(($) => $.labels.condition)}
        options={conditionOptions}
        selected={filters.conditions ?? []}
        onToggle={(value) =>
          onChange({
            ...filters,
            conditions: toggleValue(filters.conditions ?? [], value),
          })
        }
        className={className}
      />
    )
  }

  if (editor === 'size') {
    return (
      <NumberRangeInput
        label={t(($) => $.labels.sizeWithUnit)}
        min={filters.sizeMin}
        max={filters.sizeMax}
        onChange={({ min, max }) => onChange({ ...filters, sizeMin: min, sizeMax: max })}
        minPlaceholder={t(($) => $.common.inputs.min)}
        maxPlaceholder={t(($) => $.common.inputs.max)}
        unit={t(($) => $.details.sizeUnit)}
        showMin
        showMax={!!filters.sizeMax}
        minProps={{ min: locale.property.min_size, max: locale.property.max_size }}
        maxProps={{ min: locale.property.min_size, max: locale.property.max_size }}
        className={className}
        inputClassName={inputClassName}
        testIdBase="interest-size"
      />
    )
  }

  if (editor === 'rooms') {
    return (
      <NumberRangeInput
        label={t(($) => $.labels.rooms)}
        min={filters.roomsMin}
        max={filters.roomsMax}
        onChange={({ min, max }) => onChange({ ...filters, roomsMin: min, roomsMax: max })}
        minPlaceholder={t(($) => $.common.inputs.min)}
        maxPlaceholder={t(($) => $.common.inputs.max)}
        unit="h"
        showMin
        showMax={!!filters.roomsMax}
        minProps={{ min: locale.property.min_rooms, max: locale.property.max_rooms }}
        maxProps={{ min: locale.property.min_rooms, max: locale.property.max_rooms }}
        minPrefix={t(($) => $.common.inputs.min).toLowerCase()}
        maxPrefix={t(($) => $.common.inputs.max).toLowerCase()}
        className={className}
        inputClassName={inputClassName}
        testIdBase="interest-rooms"
      />
    )
  }

  if (editor === 'year') {
    const currentYear = new Date().getFullYear()
    return (
      <NumberRangeInput
        formatted={false}
        label={t(($) => $.labels.buildYear)}
        min={filters.buildYearMin ? Number(filters.buildYearMin) : undefined}
        max={filters.buildYearMax ? Number(filters.buildYearMax) : undefined}
        minProps={{ min: locale.property.min_year, max: currentYear }}
        maxProps={{ min: locale.property.min_year, max: currentYear }}
        showMin
        showMax={!!filters.buildYearMax}
        onChange={({ min, max }) =>
          onChange({
            ...filters,
            buildYearMin: min !== undefined ? String(min) : undefined,
            buildYearMax: max !== undefined ? String(max) : undefined,
          })
        }
        minPlaceholder={t(($) => $.filterEditor.minBuildYear)}
        maxPlaceholder={t(($) => $.filterEditor.maxBuildYear)}
        minPrefix={t(($) => $.filterEditor.minBuildYearPrefix)}
        maxPrefix={t(($) => $.filterEditor.maxBuildYearPrefix)}
        className={className}
        inputClassName={inputClassName}
        testIdBase="interest-year"
      />
    )
  }

  if (editor === 'price') {
    return (
      <NumberRangeInput
        min={filters.priceMin}
        max={filters.priceMax}
        minProps={{ step: 1000, min: locale.property.min_price }}
        maxProps={{ step: 1000, max: locale.property.max_price }}
        onChange={({ min, max }) => onChange({ ...filters, priceMin: min, priceMax: max })}
        label={t(($) => $.labels.price)}
        unit="€"
        type="price"
        showMin={!!filters.priceMin}
        showMax
        minPlaceholder={t(($) => $.filterEditor.minPrice)}
        maxPlaceholder={t(($) => $.filterEditor.maxPrice)}
        formatted
        className={className}
        inputClassName={inputClassName}
        testIdBase="interest-price"
      />
    )
  }

  return null
}

function toggleValue<T>(arr: T[], value: T) {
  return arr.includes(value) ? arr.filter((item) => item !== value) : [value, ...arr]
}
